import React, { useState, useEffect } from 'react';
import { FiX, FiAlertTriangle, FiCheck } from 'react-icons/fi';

const HoldLineItemModal = ({ isOpen, onClose, lineItem, onConfirmHold, isLoading }) => {
  const [holdReason, setHoldReason] = useState('');
  const [holdQuantity, setHoldQuantity] = useState(1);
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
  const [useSerialSelection, setUseSerialSelection] = useState(false);

  // Reset state when modal opens with new lineItem
  useEffect(() => {
    if (isOpen && lineItem) {
      const availableQty = (lineItem.qtyOrdered || 0) - (lineItem.qtyReceived || 0) - (lineItem.qtyOnHold || 0);
      setHoldQuantity(Math.min(1, availableQty));
      setSelectedSerialNumbers([]);
      setUseSerialSelection(false);
    }
  }, [isOpen, lineItem]);

  const handleConfirm = () => {
    if (!holdReason.trim()) {
      return; // Don't allow empty reason
    }

    if (holdQuantity <= 0) {
      return; // Don't allow zero or negative quantity
    }

    // Prepare hold data
    const holdData = {
      holdReason: holdReason.trim(),
      holdQuantity: holdQuantity,
      serialNumbers: useSerialSelection ? selectedSerialNumbers : null
    };

    onConfirmHold(holdData);
  };

  const handleClose = () => {
    setHoldReason('');
    setHoldQuantity(1);
    setSelectedSerialNumbers([]);
    setUseSerialSelection(false);
    onClose();
  };

  const handleSerialNumberToggle = (serialNumber) => {
    setSelectedSerialNumbers(prev => {
      if (prev.includes(serialNumber)) {
        return prev.filter(sn => sn !== serialNumber);
      } else {
        return [...prev, serialNumber];
      }
    });
  };

  if (!isOpen || !lineItem) return null;

  const availableQty = (lineItem.qtyOrdered || 0) - (lineItem.qtyReceived || 0) - (lineItem.qtyOnHold || 0);
  const hasSerialNumbers = lineItem.assets && lineItem.assets.length > 0;
  const pendingAssets = hasSerialNumbers ? lineItem.assets.filter(asset => asset.status === 'PENDING_RECEIVE') : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center" role="dialog" aria-modal="true" aria-labelledby="hold-modal-title">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 id="hold-modal-title" className="text-xl font-bold text-gray-900">Place Item on Hold</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-800" aria-label="Close dialog">
            <FiX size={24} />
          </button>
        </div>
        
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start">
            <FiAlertTriangle className="text-yellow-600 mr-2 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-sm text-gray-700 mb-2">
                You are about to place some or all of the following item on hold:
              </p>
              <div className="text-sm font-medium text-gray-900">
                <p><strong>SKU:</strong> {lineItem?.productSku}</p>
                <p><strong>Product:</strong> {lineItem?.productName}</p>
                <p><strong>Total Ordered:</strong> {lineItem?.qtyOrdered}</p>
                <p><strong>Already Received:</strong> {lineItem?.qtyReceived || 0}</p>
                <p><strong>Currently on Hold:</strong> {lineItem?.qtyOnHold || 0}</p>
                <p><strong>Available to Hold:</strong> <span className="text-yellow-600 font-semibold">{availableQty}</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Quantity Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity to Place on Hold <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="number"
              min="1"
              max={availableQty}
              value={holdQuantity}
              onChange={(e) => setHoldQuantity(parseInt(e.target.value) || 1)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              disabled={isLoading || useSerialSelection}
            />
            <span className="text-sm text-gray-500">
              of {availableQty} available units
            </span>
            {holdQuantity === availableQty && (
              <span className="text-sm text-red-600 font-medium">
                (All available units)
              </span>
            )}
          </div>
        </div>

        {/* Serial Number Selection (if available) */}
        {hasSerialNumbers && pendingAssets.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="useSerialSelection"
                checked={useSerialSelection}
                onChange={(e) => {
                  setUseSerialSelection(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedSerialNumbers([]);
                  }
                }}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                disabled={isLoading}
              />
              <label htmlFor="useSerialSelection" className="ml-2 text-sm font-medium text-gray-700">
                Select specific serial numbers to hold
              </label>
            </div>

            {useSerialSelection && (
              <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                <p className="text-xs text-gray-500 mb-2">
                  Select {holdQuantity} serial number{holdQuantity !== 1 ? 's' : ''} to place on hold:
                </p>
                <div className="space-y-2">
                  {pendingAssets.map((asset) => (
                    <div key={asset.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`serial-${asset.id}`}
                        checked={selectedSerialNumbers.includes(asset.serialNumber)}
                        onChange={() => handleSerialNumberToggle(asset.serialNumber)}
                        disabled={
                          isLoading || 
                          (!selectedSerialNumbers.includes(asset.serialNumber) && selectedSerialNumbers.length >= holdQuantity)
                        }
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`serial-${asset.id}`} className="ml-2 text-sm text-gray-700">
                        {asset.serialNumber}
                        {asset.macAddress && (
                          <span className="text-gray-500 text-xs ml-2">
                            (MAC: {asset.macAddress})
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {selectedSerialNumbers.length} of {holdQuantity} required
                </p>
              </div>
            )}
          </div>
        )}

        {/* Hold Reason */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hold Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            placeholder="Please provide a reason for placing this item on hold (e.g., damaged packaging, wrong item, quality issue)..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            maxLength={500}
            disabled={isLoading}
          />
          <p className="text-xs text-gray-500 mt-1">
            {holdReason.length}/500 characters
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
            disabled={
              isLoading || 
              !holdReason.trim() || 
              holdQuantity <= 0 || 
              holdQuantity > availableQty ||
              (useSerialSelection && selectedSerialNumbers.length !== holdQuantity)
            }
          >
            {isLoading ? 'Processing...' : `Place ${holdQuantity} Unit${holdQuantity !== 1 ? 's' : ''} on Hold`}
          </button>
        </div>

        {/* Show remaining quantities after hold */}
        {holdQuantity > 0 && holdQuantity <= availableQty && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 font-medium mb-1">After placing on hold:</p>
            <div className="text-xs text-blue-700 space-y-1">
              <p>• {holdQuantity} unit{holdQuantity !== 1 ? 's' : ''} will be placed on hold</p>
              <p>• {availableQty - holdQuantity} unit{availableQty - holdQuantity !== 1 ? 's' : ''} will remain available to receive</p>
              <p>• Total on hold: {(lineItem.qtyOnHold || 0) + holdQuantity} unit{((lineItem.qtyOnHold || 0) + holdQuantity) !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HoldLineItemModal; 